import type { SupplierOrderStatus } from '../supplierOrderStatuses';
import { formatProductDimensions } from '../../../utils/productDimensions';

/**
 * Karta ZD według makiety (owner 2026-10-05: „Każde zamówienie powinno być tą
 * makietą — różnią się jedynie etapem”). One home for the stages of a ZD card
 * and for the task list of its first stage („Szkic”): a new ZD, a saved draft
 * and a draft from a customer-order confirmation are the same card in stage 1.
 *
 * The detailed supplier-order status is unchanged in the data; the five stages
 * only group it (makieta `STAGES`). Later stages keep today's card (decision 5
 * of the task) — only the first stage has a task list here.
 */
export const SUPPLIER_ORDER_STAGES: ReadonlyArray<{ key: SupplierOrderStageKey; statuses: SupplierOrderStatus[] }> = [
  { key: 'draft', statuses: ['DRAFT'] },
  { key: 'ordered', statuses: ['SENT', 'CONFIRMED'] },
  { key: 'onTheWay', statuses: ['IN_PRODUCTION', 'READY', 'SHIPPED'] },
  { key: 'received', statuses: ['PARTIALLY_RECEIVED', 'RECEIVED'] },
  { key: 'completed', statuses: ['INVOICED'] },
];

export type SupplierOrderStageKey = 'draft' | 'ordered' | 'onTheWay' | 'received' | 'completed';

/** Index of the stage of a status; -1 for Wstrzymane / Anulowane (no stage). */
export const getSupplierOrderStageIndex = (status?: string | null): number => (
  SUPPLIER_ORDER_STAGES.findIndex((stage) => stage.statuses.includes(status as SupplierOrderStatus))
);

export interface SupplierOrderDraftLine {
  name?: string | null;
  quantity?: number | string | null;
  /** Absent when the viewer cannot see purchase prices (backend redaction). */
  unitCost?: number | string | null;
  width?: number | string | null;
  height?: number | string | null;
  length?: number | string | null;
  thickness?: number | string | null;
  dimensionDisplayProfile?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
}

/** „Nazwa (gr. 12 x szer. 1250 x dł. 3000 mm)”: same-name lines of a ZD stay distinguishable in the task text. */
export function supplierOrderDraftLineLabel(line: SupplierOrderDraftLine): string {
  const name = String(line.name ?? '').trim();
  const dimensions = formatProductDimensions(line);
  return dimensions ? `${name} (${dimensions})` : name;
}

export interface SupplierOrderDraftFacts {
  supplierName: string | null;
  lines: SupplierOrderDraftLine[];
  /** Purchase prices are visible to this viewer (else the price is not checked). */
  pricesVisible: boolean;
  /** A draft written by a customer-order confirmation (migration 197). */
  fromCustomerOrder: boolean;
  /** Planned ready date (ISO), shown as information only. */
  expectedDelivery?: string | null;
}

export type SupplierOrderDraftTaskKey = 'supplier' | 'lines' | 'origin' | 'expectedDelivery';
export type SupplierOrderDraftAction = 'chooseSupplier' | 'lines';

export interface SupplierOrderDraftTask {
  key: SupplierOrderDraftTaskKey;
  done: boolean;
  /** Information, never blocks (makieta `info`). */
  info?: boolean;
  /** The task's own action; on a done task it is offered as „Zmień …”. */
  action?: SupplierOrderDraftAction;
}

export interface SupplierOrderDraftChecklist {
  tasks: SupplierOrderDraftTask[];
  /** Lines without a positive quantity / without a price (names for the hint). */
  linesWithoutQuantity: number;
  linesWithoutPrice: string[];
  /** Action of the first open task (makieta `primaryKey()`), null when all done. */
  primaryAction: SupplierOrderDraftAction | null;
  /** Open tasks that keep „Wyślij do dostawcy” disabled. */
  blocking: SupplierOrderDraftTaskKey[];
  canSend: boolean;
}

const positive = (value: number | string | null | undefined): boolean => {
  if (value === null || value === undefined || value === '') return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

/**
 * Makieta `checklist()` for step 0: the supplier is chosen, every line has a
 * quantity and (for a viewer who sees purchase prices) a price. The origin of a
 * confirmation draft and the planned ready date are information only. Sending
 * waits for every open task; the manual status change goes around the list,
 * as on the ZK card.
 */
export function buildSupplierOrderDraftChecklist(facts: SupplierOrderDraftFacts): SupplierOrderDraftChecklist {
  const lines = facts.lines.filter((line) => String(line.name ?? '').trim() || positive(line.quantity));
  const linesWithoutQuantity = lines.filter((line) => !positive(line.quantity)).length;
  const linesWithoutPrice = facts.pricesVisible
    ? lines.filter((line) => !positive(line.unitCost)).map(supplierOrderDraftLineLabel)
    : [];
  const supplierDone = Boolean(facts.supplierName);
  const linesDone = lines.length > 0 && linesWithoutQuantity === 0 && linesWithoutPrice.length === 0;

  const tasks: SupplierOrderDraftTask[] = [
    { key: 'supplier', done: supplierDone, action: 'chooseSupplier' },
    { key: 'lines', done: linesDone, action: 'lines' },
  ];
  if (facts.fromCustomerOrder) tasks.push({ key: 'origin', done: true, info: true });
  if (facts.expectedDelivery) tasks.push({ key: 'expectedDelivery', done: true, info: true });

  const blocking = tasks.filter((task) => !task.done && !task.info).map((task) => task.key);
  const firstOpen = tasks.find((task) => !task.done && !task.info && task.action);
  return {
    tasks,
    linesWithoutQuantity,
    linesWithoutPrice,
    primaryAction: firstOpen?.action ?? null,
    blocking,
    canSend: blocking.length === 0,
  };
}
